import { PORTAL_MESSAGE_MAX, PORTAL_MESSAGES, type PortalRequestDetail } from '@fos/shared/contracts/portal';
import { ArrowLeft, Compass, Flag, MessageSquare, XCircle } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { appErrorCode, errorMessage } from '../../../lib/api';
import { formatDateShort } from '../../../lib/format';
import { trpc } from '../../../lib/trpc';
import { FormMessage } from '../../../ui/Blocks';
import { Button } from '../../../ui/Button';
import { Dialog } from '../../../ui/Dialog';
import { Field } from '../../../ui/Field';
import { Textarea } from '../../../ui/Input';
import { Mono } from '../../../ui/Mono';
import { Page } from '../../../ui/Page';
import { Section } from '../../../ui/Section';
import { EmptyState, ErrorState, Skeleton } from '../../../ui/States';
import { toast } from '../../../ui/Toast';
import { type ConversationItem, ConversationList, MessageComposer } from '../components/Conversation';
import { PORTAL_STATUS_EXPLAINED, PortalStatusPill } from '../components/PortalStatus';

/**
 * Portal klienta — szczegóły zgłoszenia (`/portal/zgloszenia/:id`, link z maila do osoby klienta): tytuł, numer,
 * status po ludzku z jednym zdaniem wyjaśnienia, opis, rozmowa z zespołem (dymki: klient po prawej, zespół po lewej
 * z imieniem) i pole wiadomości. „Anuluj zgłoszenie” tylko, dopóki zespół go nie zaplanuje (`canCancel`: status „Zgłoszenie”).
 * Cudze zgłoszenie albo zadanie spoza portalu = NOT_FOUND („Nie ma takiego zgłoszenia”).
 */
export function PortalRequestPage() {
  const { id = '' } = useParams();
  const request = trpc.portal.getRequest.useQuery({ id }, { enabled: id !== '' });

  if (request.isPending) {
    return (
      <Page width="reading">
        <div role="status" aria-label="Wczytywanie zgłoszenia" className="flex flex-col gap-4">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-8 w-2/3" />
          <Skeleton className="h-5 w-40" />
          <Skeleton className="mt-4 h-28 w-full" />
        </div>
      </Page>
    );
  }
  if (request.isError) {
    const notFound = appErrorCode(request.error) === 'NOT_FOUND' || appErrorCode(request.error) === 'VALIDATION_ERROR';
    return (
      <Page width="reading">
        <BackLink />
        <Section className="mt-4">
          {notFound ? (
            <EmptyState
              icon={Compass}
              title="Nie ma takiego zgłoszenia"
              description="Link mógł się zmienić albo zgłoszenie należy do innego konta."
              action={
                <Button asChild variant="secondary">
                  <Link to="/portal">Wróć do zgłoszeń</Link>
                </Button>
              }
            />
          ) : (
            <ErrorState onRetry={() => void request.refetch()} retrying={request.isFetching} />
          )}
        </Section>
      </Page>
    );
  }
  return <RequestView request={request.data} />;
}

function BackLink() {
  return (
    <Link
      to="/portal"
      className="-my-2 inline-flex min-h-11 items-center gap-1.5 rounded-xs text-body-s font-medium text-fg-secondary hover:text-fg focus-visible:outline-2 focus-visible:outline-accent"
    >
      <ArrowLeft size={16} strokeWidth={1.75} aria-hidden />
      Wszystkie zgłoszenia
    </Link>
  );
}

function RequestView({ request }: { request: PortalRequestDetail }) {
  const utils = trpc.useUtils();
  const [cancelOpen, setCancelOpen] = useState(false);
  const addMessage = trpc.portal.addMessage.useMutation({
    onSuccess: (next) => {
      utils.portal.getRequest.setData({ id: next.id }, next);
      void utils.portal.listRequests.invalidate();
    },
  });

  const items: ConversationItem[] = request.messages.map((m) => ({
    id: m.id,
    side: m.author === 'client' ? 'own' : 'other',
    // własnych wiadomości nie podpisujemy; zespół — samo imię, inna osoba klienta — imię i nazwisko
    authorName: m.mine ? null : m.authorName,
    authorNote: m.author === 'team' ? 'zespół' : undefined,
    text: m.text,
    createdAt: m.createdAt,
  }));

  return (
    <Page width="reading" className="pb-16">
      <BackLink />
      <header className="mt-3 flex flex-col gap-2">
        <h1 className="text-h3 break-words text-fg">{request.title}</h1>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-body-s text-fg-secondary">
          <PortalStatusPill status={request.status} />
          {request.urgent && (
            <span className="inline-flex items-center gap-1 text-caption font-medium text-warning">
              <Flag size={12} strokeWidth={2} aria-hidden fill="currentColor" fillOpacity={0.18} />
              Pilne
            </span>
          )}
          <span className="text-caption text-fg-muted">
            <Mono>#{request.number}</Mono> · zgłoszone {formatDateShort(request.createdAt)}
          </span>
        </div>
        <p className="text-body-s text-fg-secondary">
          {PORTAL_STATUS_EXPLAINED[request.status]}
          {request.leadFirstName && ` Prowadzi: ${request.leadFirstName}.`}
        </p>
      </header>

      <Section className="mt-5" title="Opis" tone="strong">
        {request.description ? (
          <p className="text-body-s whitespace-pre-wrap break-words text-fg">{request.description}</p>
        ) : (
          <p className="text-body-s text-fg-muted">Bez opisu.</p>
        )}
      </Section>

      <Section className="mt-4" title="Rozmowa" tone="strong">
        {items.length > 0 ? (
          <ConversationList items={items} className="mb-5" />
        ) : (
          <p className="mb-4 flex items-start gap-2 text-body-s text-fg-muted">
            <MessageSquare size={16} strokeWidth={1.75} aria-hidden className="mt-0.5 shrink-0" />
            {request.canWrite ? 'Tu pojawią się odpowiedzi zespołu. Możesz też dopisać szczegóły.' : 'Brak wiadomości.'}
          </p>
        )}
        {request.canWrite ? (
          <MessageComposer
            label="Twoja wiadomość"
            placeholder="Napisz wiadomość do zespołu…"
            maxLength={PORTAL_MESSAGE_MAX}
            sending={addMessage.isPending}
            hint="Zespół zobaczy ją przy zgłoszeniu."
            onSend={(text) => addMessage.mutateAsync({ requestId: request.id, text })}
          />
        ) : (
          <p className="rounded-lg bg-surface px-4 py-3 text-body-s text-fg-secondary">
            {PORTAL_MESSAGES.closedRequest}
          </p>
        )}
      </Section>

      {request.canCancel && (
        <div className="mt-6 flex flex-col items-start gap-1">
          <Button variant="ghost" icon={XCircle} onClick={() => setCancelOpen(true)} className="max-md:h-11">
            Anuluj zgłoszenie
          </Button>
          <p className="text-caption text-fg-muted">Możesz je anulować, dopóki zespół go nie zaplanuje.</p>
        </div>
      )}
      <CancelDialog request={request} open={cancelOpen} onOpenChange={setCancelOpen} />
    </Page>
  );
}

function CancelDialog({
  request,
  open,
  onOpenChange,
}: {
  request: PortalRequestDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const utils = trpc.useUtils();
  const [note, setNote] = useState('');
  const cancel = trpc.portal.cancelRequest.useMutation({
    onSuccess: (next) => {
      utils.portal.getRequest.setData({ id: next.id }, next);
      void utils.portal.listRequests.invalidate();
      toast.success('Zgłoszenie anulowane');
      onOpenChange(false);
      setNote('');
    },
  });
  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v);
        if (!v) cancel.reset();
      }}
      title="Anulować zgłoszenie?"
      description={`#${request.number} ${request.title} — trafi do zamkniętych.`}
      width="s"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Wróć
          </Button>
          <Button
            variant="primary"
            loading={cancel.isPending}
            onClick={() => cancel.mutate({ id: request.id, ...(note.trim() ? { note: note.trim() } : {}) })}
          >
            Anuluj zgłoszenie
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Field label="Powód (opcjonalnie)" hint="Zespół zobaczy go w rozmowie.">
          <Textarea rows={3} value={note} maxLength={1000} onChange={(e) => setNote(e.target.value)} />
        </Field>
        {cancel.error && <FormMessage>{errorMessage(cancel.error)}</FormMessage>}
      </div>
    </Dialog>
  );
}
